/**
 * Feb 2026 · Manager Tree Picker
 *
 * Replacement for the old "View as" <Select> on /app/tasks. Renders the
 * workspace org tree as a searchable, expand/collapse tree control.
 *
 * Scope is decided by the backend on `GET /workspaces/{ws}/tasks-rollup/managers-tree`:
 *   • task.view_org_tree / workspace.admin → full workspace org chart
 *   • task.view_reports_tree only          → caller's subtree
 *
 * Rendering is FLAT (iterative) not recursive JSX — avoids a Babel
 * traversal blowup we hit on the recursive version. We flatten the
 * visible tree into an ordered list of `{uid, depth, hasKids, expanded}`
 * entries and render each in a single `.map`.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ChevronRight, ChevronDown, Search, User, Check, Wrench, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ManagerTreePicker({ wsId, viewerUserId, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [tree, setTree] = useState({ scope: null, nodes: [], root_user_ids: [], viewer_id: null, has_legacy_records: false, legacy_record_count: 0 });
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [allowed, setAllowed] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [confirmMigrate, setConfirmMigrate] = useState(false);

  const refetch = () => {
    if (!wsId) return Promise.resolve();
    return api.get(`/workspaces/${wsId}/tasks-rollup/managers-tree`)
       .then((r) => setTree(r.data || { nodes: [], root_user_ids: [] }))
       .catch((e) => { setAllowed(e?.response?.status !== 403); });
  };

  useEffect(() => {
    if (!wsId) return;
    let alive = true;
    api.get(`/workspaces/${wsId}/tasks-rollup/managers-tree`)
       .then((r) => { if (alive) setTree(r.data || { nodes: [], root_user_ids: [] }); })
       .catch((e) => { if (alive) setAllowed(e?.response?.status !== 403); });
    return () => { alive = false; };
  }, [wsId]);

  const runMigration = async () => {
    if (!wsId || migrating) return;
    setMigrating(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/tasks-rollup/managers-tree/migrate-legacy`);
      const d = r.data || {};
      const migrated = d.migrated || 0;
      const remaining = d.remaining_legacy || 0;
      if (migrated > 0) {
        toast.success(`Org chart fixed · ${migrated} record${migrated === 1 ? "" : "s"} migrated`);
      } else {
        toast.info("Org chart already clean · nothing to migrate");
      }
      if (remaining > 0) {
        toast.warning(`${remaining} row${remaining === 1 ? "" : "s"} still unresolved (manager not found in this workspace)`);
      }
      await refetch();
      setConfirmMigrate(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  const nodeMap = useMemo(() => {
    const m = new Map();
    (tree.nodes || []).forEach((n) => m.set(n.user_id, n));
    return m;
  }, [tree.nodes]);

  // Search filter: null → show all, else set of visible uids
  // (matches + all ancestors of matches).
  const visibleIds = useMemo(() => {
    const nodes = tree.nodes || [];
    if (!q.trim()) return null;
    const term = q.trim().toLowerCase();
    const matches = new Set();
    nodes.forEach((n) => {
      const hay = ((n.name || "") + " " + (n.email || "")).toLowerCase();
      if (hay.includes(term)) matches.add(n.user_id);
    });
    const visible = new Set(matches);
    matches.forEach((uid) => {
      let cur = nodeMap.get(uid);
      while (cur && cur.manager_user_id) {
        visible.add(cur.manager_user_id);
        cur = nodeMap.get(cur.manager_user_id);
      }
    });
    return visible;
  }, [q, tree.nodes, nodeMap]);

  // Auto-expand ancestors of search matches.
  useEffect(() => {
    if (!visibleIds) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((uid) => {
        const n = nodeMap.get(uid);
        if (n && (n.child_user_ids || []).length) next.add(uid);
      });
      return next;
    });
  }, [visibleIds, nodeMap]);

  // Iterative flattening of the visible tree into an ordered list.
  const flatRows = useMemo(() => {
    const out = [];
    const roots = (tree.root_user_ids || []).filter((uid) => !visibleIds || visibleIds.has(uid));
    const stack = [];
    // Push in reverse so first root is popped first.
    for (let i = roots.length - 1; i >= 0; i--) {
      stack.push({ uid: roots[i], depth: 0 });
    }
    while (stack.length) {
      const { uid, depth } = stack.pop();
      const n = nodeMap.get(uid);
      if (!n) continue;
      const kids = (n.child_user_ids || []).filter((cid) => !visibleIds || visibleIds.has(cid));
      const hasKids = kids.length > 0;
      const isExpanded = expanded.has(uid);
      out.push({
        uid, depth, hasKids, isExpanded,
        name: n.name, email: n.email,
        own_active: n.own_active || 0,
        own_overdue: n.own_overdue || 0,
        subtree_active: n.subtree_active || 0,
        subtree_overdue: n.subtree_overdue || 0,
        // Whether the source node has ANY children (regardless of search
        // filter) — badges use this so counts always reflect real
        // subtree workload, not what's currently visible in the popover.
        hasKidsInSource: (n.child_user_ids || []).length > 0,
      });
      if (hasKids && isExpanded) {
        for (let i = kids.length - 1; i >= 0; i--) {
          stack.push({ uid: kids[i], depth: depth + 1 });
        }
      }
    }
    return out;
  }, [tree.root_user_ids, nodeMap, expanded, visibleIds]);

  const toggle = (uid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const select = (val) => {
    onChange?.(val);
    setOpen(false);
    setQ("");
  };

  const label = useMemo(() => {
    if (!value || value === "self") return "Myself";
    const n = nodeMap.get(value);
    return n?.name || n?.email || "Selected member";
  }, [value, nodeMap]);

  if (!allowed) return null;
  const hasAnyNodes = (tree.nodes || []).length > 0;
  if (!hasAnyNodes) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-7 text-[11px] rounded-none border border-border bg-background hover:bg-muted px-2 inline-flex items-center gap-1.5 w-64 justify-between"
          data-testid="tasks-team-rollup-viewas-tree-trigger">
          <span className="truncate flex items-center gap-1.5">
            <User className="w-3 h-3 opacity-60" />
            {label}
          </span>
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-80 p-0 rounded-none border border-border"
        data-testid="tasks-team-rollup-viewas-tree-popover">
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 opacity-60 pointer-events-none" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search reports…"
              className="h-7 text-[11px] rounded-none pl-6"
              data-testid="tasks-team-rollup-viewas-tree-search"
            />
          </div>
          <div className="mt-1 flex items-center gap-2">
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
              {tree.scope === "org" ? "Entire workspace tree" : "My reports"}
            </div>
            {tree.has_legacy_records && !confirmMigrate && (
              <button
                type="button"
                onClick={() => setConfirmMigrate(true)}
                className="ml-auto inline-flex items-center gap-1 h-5 px-1.5 border border-amber-500/60 bg-amber-500/10 text-amber-700 text-[9px] uppercase tracking-widest hover:bg-amber-500/20"
                data-testid="tasks-team-rollup-viewas-tree-fix-orgchart"
                title={`Legacy schema detected · ${tree.legacy_record_count} record(s). Click to rewrite.`}>
                <Wrench className="w-2.5 h-2.5" />
                Fix org chart
                <span className="font-mono tabular-nums opacity-80">·{tree.legacy_record_count}</span>
              </button>
            )}
          </div>
          {tree.has_legacy_records && confirmMigrate && (
            <div className="mt-1 p-1.5 border border-amber-500/60 bg-amber-500/5 text-[10px] flex items-center gap-1.5"
                 data-testid="tasks-team-rollup-viewas-tree-fix-orgchart-confirm">
              <span className="text-amber-800 leading-tight flex-1">
                Rewrite {tree.legacy_record_count} legacy row{tree.legacy_record_count === 1 ? "" : "s"} to the modern schema?
              </span>
              <button
                type="button"
                onClick={runMigration}
                disabled={migrating}
                className="h-5 px-2 border border-amber-600 bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 inline-flex items-center gap-1"
                data-testid="tasks-team-rollup-viewas-tree-fix-orgchart-run">
                {migrating && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                Run
              </button>
              <button
                type="button"
                onClick={() => setConfirmMigrate(false)}
                disabled={migrating}
                className="h-5 px-2 border border-border bg-background hover:bg-muted disabled:opacity-60"
                data-testid="tasks-team-rollup-viewas-tree-fix-orgchart-cancel">
                Cancel
              </button>
            </div>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          <button
            type="button"
            onClick={() => select("self")}
            className={"w-full text-left px-2 py-1.5 text-[12px] border-b border-border hover:bg-muted inline-flex items-center gap-2 " + ((!value || value === "self") ? "bg-muted font-semibold" : "")}
            data-testid="tasks-team-rollup-viewas-tree-myself">
            <Check className={"w-3 h-3 " + ((!value || value === "self") ? "opacity-100" : "opacity-0")} />
            Myself
          </button>
          {flatRows.length === 0 ? (
            <div className="px-3 py-4 text-[11px] text-muted-foreground" data-testid="tasks-team-rollup-viewas-tree-empty">
              {q ? "No matches." : "No reports found."}
            </div>
          ) : (
            <div className="py-1">
              {flatRows.map((row) => {
                const isSelected = value && value !== "self" && value === row.uid;
                const isMe = row.uid === viewerUserId;
                return (
                  <div
                    key={row.uid}
                    className={"flex items-center gap-1 text-[12px] hover:bg-muted " + (isSelected ? "bg-muted font-semibold" : "")}
                    style={{ paddingLeft: (8 + row.depth * 14) + "px" }}>
                    {row.hasKids ? (
                      <button
                        type="button"
                        onClick={() => toggle(row.uid)}
                        className="p-1 hover:bg-foreground/10 shrink-0"
                        data-testid={"tasks-team-rollup-viewas-tree-toggle-" + row.uid}
                        aria-label={row.isExpanded ? "Collapse" : "Expand"}>
                        {row.isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      </button>
                    ) : (
                      <span className="w-5 h-5 inline-block shrink-0" />
                    )}
                    <button
                      type="button"
                      onClick={() => select(row.uid)}
                      className="flex-1 text-left py-1.5 pr-2 truncate inline-flex items-center gap-1.5"
                      data-testid={"tasks-team-rollup-viewas-tree-node-" + row.uid}>
                      <Check className={"w-3 h-3 shrink-0 " + (isSelected ? "opacity-100" : "opacity-0")} />
                      <span className="truncate">{row.name || row.email || row.uid.slice(0, 8)}</span>
                      {isMe && <span className="text-[9px] uppercase tracking-widest text-muted-foreground">you</span>}
                      <NodeBadges row={row} className="ml-auto" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Feb 2026 · Mini heat-map badges next to each tree node so managers can
// spot loaded reports without drilling in. When a node has descendants,
// we show subtree totals (which include the node's own tasks); otherwise
// just the own count. Overdue > 0 renders in rose-600.
function NodeBadges({ row, className = "" }) {
  const hasKids = row.hasKidsInSource;
  const active = hasKids ? row.subtree_active : row.own_active;
  const overdue = hasKids ? row.subtree_overdue : row.own_overdue;
  if (!active && !overdue) return null;
  return (
    <span className={"inline-flex items-center gap-1 shrink-0 " + className}
          data-testid={"tasks-team-rollup-viewas-tree-badges-" + row.uid}
          title={hasKids
            ? `Subtree · ${active} active, ${overdue} overdue`
            : `Own · ${active} active, ${overdue} overdue`}>
      {active > 0 && (
        <span className="text-[9px] font-mono tabular-nums px-1 py-0 border border-border bg-background text-muted-foreground leading-tight">
          {active}
        </span>
      )}
      {overdue > 0 && (
        <span className="text-[9px] font-mono tabular-nums px-1 py-0 border border-rose-500/50 bg-rose-500/10 text-rose-700 leading-tight"
              data-testid={"tasks-team-rollup-viewas-tree-badge-overdue-" + row.uid}>
          {overdue}
        </span>
      )}
    </span>
  );
}
